/**
 * SignalTriggerConfig - Campaign signal trigger toggle panel.
 */

import { FC, useCallback, useEffect, useState } from 'react';
import { emptyBaseSplitApi } from '@api/baseApi';
import styled from 'styled-components';

// RTK Query endpoints
const signalApi = emptyBaseSplitApi.injectEndpoints({
  endpoints: build => ({
    getSignalConfig: build.query<SignalConfigResponse, string>({
      query: campaignId => `/api/signals/campaigns/${campaignId}/config/`,
    }),
    updateSignalConfig: build.mutation<
      SignalConfigResponse,
      { campaignId: string; signals: Record<string, SignalConfig> }
    >({
      query: ({ campaignId, signals }) => ({
        url: `/api/signals/campaigns/${campaignId}/config/`,
        method: 'PUT',
        body: { signals },
      }),
    }),
  }),
  overrideExisting: true,
});

const { useGetSignalConfigQuery, useUpdateSignalConfigMutation } = signalApi;

interface SignalConfig {
  enabled: boolean;
  label: string;
  category: string;
  description: string;
  urgency: string;
  opening_line: string;
}

interface SignalConfigResponse {
  inferred_industry: string;
  signals: Record<string, SignalConfig>;
}

const CATEGORY_ORDER = ['listing', 'reputation', 'social', 'linkedin', 'hiring', 'contact', 'bbb', 'news'];

const CATEGORY_META: Record<string, { label: string; icon: string }> = {
  listing: { label: 'Listing Signals', icon: '\u{1F3E0}' },
  reputation: { label: 'Reputation Signals', icon: '\u2B50' },
  linkedin: { label: 'LinkedIn Signals', icon: '\u{1F4BC}' },
  hiring: { label: 'Hiring Signals', icon: '\u{1F4CB}' },
  contact: { label: 'Contact Changes', icon: '\u{1F4DE}' },
  social: { label: 'Social Media Signals', icon: '\u{1F4F1}' },
  bbb: { label: 'BBB Signals', icon: '\u{1F3DB}' },
  news: { label: 'News Signals', icon: '\u{1F4F0}' },
};

const INDUSTRY_LABELS: Record<string, string> = {
  real_estate: 'Real Estate',
  legal: 'Legal',
  healthcare: 'Healthcare',
  restaurants_hospitality: 'Restaurants & Hospitality',
  home_services: 'Home Services',
  tech_saas: 'Tech / SaaS',
  marketing_agency: 'Marketing Agency',
  recruiting: 'Recruiting',
  finance: 'Finance',
  construction: 'Construction',
  generic: 'General',
};

// Styled components (must be before component to satisfy no-use-before-define)
const Wrapper = styled.div`
  background: var(--sk-surface-1, #fff);
  border: 1px solid var(--sk-border, #e5e7eb);
  border-radius: 12px;
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--sk-border, #e5e7eb);
`;

const HeaderInfo = styled.div``;

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--sk-text-primary, #111827);
`;

const Subtitle = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  color: var(--sk-text-secondary, #6b7280);
`;

const SaveButton = styled.button<{ disabled?: boolean }>`
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  background: var(--sk-cyan-500, #06b6d4);
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: ${p => (p.disabled ? 'wait' : 'pointer')};
  opacity: ${p => (p.disabled ? 0.7 : 1)};
  transition: opacity 0.15s;
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

const LoadingText = styled.div`
  padding: 20px;
  text-align: center;
  color: var(--sk-text-secondary, #6b7280);
`;

const CategorySection = styled.div`
  margin-bottom: 20px;
`;

const CategoryTitle = styled.h4`
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 600;
  color: var(--sk-text-secondary, #6b7280);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SignalRow = styled.div<{ $enabled: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 12px;
  margin-bottom: 4px;
  border-radius: 8px;
  background: ${p => (p.$enabled ? 'var(--sk-cyan-50, #ecfeff)' : 'var(--sk-surface-2, #f9fafb)')};
  border: 1px solid ${p => (p.$enabled ? 'var(--sk-cyan-200, #a5f3fc)' : 'var(--sk-border, #e5e7eb)')};
  transition: all 0.15s ease;
`;

const Toggle = styled.button<{ $active: boolean }>`
  width: 40px;
  height: 22px;
  border-radius: 11px;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
  margin-top: 2px;
  position: relative;
  background: ${p => (p.$active ? 'var(--sk-cyan-500, #06b6d4)' : '#d1d5db')};
  transition: background 0.2s ease;
  padding: 0;
`;

const ToggleThumb = styled.span<{ $active: boolean }>`
  position: absolute;
  top: 2px;
  left: ${p => (p.$active ? '20px' : '2px')};
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  transition: left 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
`;

const SignalContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const SignalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SignalLabel = styled.span<{ $enabled: boolean }>`
  font-size: 14px;
  font-weight: 500;
  color: ${p => (p.$enabled ? 'var(--sk-cyan-700, #0e7490)' : 'var(--sk-text-secondary, #6b7280)')};
`;

const SignalDesc = styled.p<{ $enabled: boolean }>`
  margin: 2px 0 0;
  font-size: 12px;
  color: ${p => (p.$enabled ? 'var(--sk-text-secondary, #4b5563)' : '#9ca3af')};
`;

const OpeningLine = styled.p`
  margin: 4px 0 0;
  font-size: 11px;
  color: var(--sk-text-secondary, #6b7280);
  font-style: italic;
  line-height: 1.4;
`;

const UrgencySelect = styled.select`
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid var(--sk-cyan-200, #a5f3fc);
  background: var(--sk-cyan-50, #eff6ff);
  color: var(--sk-cyan-700, #0e7490);
`;

const FooterInfo = styled.div`
  margin-top: 16px;
  padding: 12px;
  border-radius: 8px;
  background: var(--sk-surface-2, #f9fafb);
  border: 1px solid var(--sk-border, #e5e7eb);
  font-size: 12px;
  color: var(--sk-text-secondary, #6b7280);
  line-height: 1.5;
`;

// Component
interface Props {
  campaignId: string;
}

const SignalTriggerConfig: FC<Props> = ({ campaignId }) => {
  const { data: config, isLoading, error } = useGetSignalConfigQuery(campaignId);
  const [updateConfig, { isLoading: isSaving }] = useUpdateSignalConfigMutation();
  const [localConfig, setLocalConfig] = useState<Record<string, SignalConfig> | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (config?.signals) {
      setLocalConfig(config.signals);
      setDirty(false);
    }
  }, [config]);

  const toggleSignal = useCallback(
    (signalType: string) => {
      if (!localConfig) return;
      setLocalConfig(prev => ({
        ...prev!,
        [signalType]: { ...prev![signalType], enabled: !prev![signalType].enabled },
      }));
      setDirty(true);
    },
    [localConfig],
  );

  const updateUrgency = useCallback(
    (signalType: string, urgency: string) => {
      if (!localConfig) return;
      setLocalConfig(prev => ({
        ...prev!,
        [signalType]: { ...prev![signalType], urgency },
      }));
      setDirty(true);
    },
    [localConfig],
  );

  const handleSave = useCallback(async () => {
    if (!localConfig) return;
    await updateConfig({ campaignId, signals: localConfig });
    setDirty(false);
  }, [localConfig, campaignId, updateConfig]);

  if (isLoading)
    return (
      <Wrapper>
        <LoadingText>Loading signal configuration...</LoadingText>
      </Wrapper>
    );
  if (error || !localConfig || !config) return null;

  // Group signals by category using reduce instead of for-of
  const grouped = Object.entries(localConfig).reduce<Record<string, Array<[string, SignalConfig]>>>(
    (acc, [type, sig]) => {
      const cat = sig.category || 'other';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push([type, sig]);
      return acc;
    },
    {},
  );

  const enabledCount = Object.values(localConfig).filter(s => s.enabled).length;
  const totalCount = Object.keys(localConfig).length;

  return (
    <Wrapper>
      <Header>
        <HeaderInfo>
          <Title>Signal Triggers</Title>
          <Subtitle>
            Auto-detected: <strong>{INDUSTRY_LABELS[config.inferred_industry] || config.inferred_industry}</strong>
            {' \u00B7 '}
            {enabledCount} of {totalCount} enabled
          </Subtitle>
        </HeaderInfo>
        {dirty && (
          <SaveButton onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Changes'}
          </SaveButton>
        )}
      </Header>

      {CATEGORY_ORDER.map(catKey => {
        const signals = grouped[catKey];
        if (!signals || signals.length === 0) return null;
        const meta = CATEGORY_META[catKey] || { label: catKey, icon: '' };

        return (
          <CategorySection key={catKey}>
            <CategoryTitle>
              {meta.icon} {meta.label}
            </CategoryTitle>
            {signals.map(([type, sig]) => (
              <SignalRow key={type} $enabled={sig.enabled}>
                <Toggle $active={sig.enabled} onClick={() => toggleSignal(type)}>
                  <ToggleThumb $active={sig.enabled} />
                </Toggle>
                <SignalContent>
                  <SignalHeader>
                    <SignalLabel $enabled={sig.enabled}>{sig.label}</SignalLabel>
                    {sig.enabled && (
                      <UrgencySelect value={sig.urgency} onChange={e => updateUrgency(type, e.target.value)}>
                        <option value="immediate">Immediate</option>
                        <option value="this_week">This Week</option>
                        <option value="this_month">This Month</option>
                      </UrgencySelect>
                    )}
                  </SignalHeader>
                  <SignalDesc $enabled={sig.enabled}>{sig.description}</SignalDesc>
                  {sig.enabled && sig.opening_line && (
                    <OpeningLine>&ldquo;{sig.opening_line.substring(0, 140)}...&rdquo;</OpeningLine>
                  )}
                </SignalContent>
              </SignalRow>
            ))}
          </CategorySection>
        );
      })}

      <FooterInfo>
        Enabled signals automatically trigger the next email in your sequence when detected for leads in this campaign.
      </FooterInfo>
    </Wrapper>
  );
};

export default SignalTriggerConfig;
